/**
 * NORDBETT24 | GN Produktkarten-Ausrichtung V2 (global)
 *
 * Gleiche Preis- und Button-Oberkanten INNERHALB DERSELBEN VISUELLEN REIHE.
 * Gilt fuer Horizon-Kollektionen, Suche, Startseiten-Karten, Empfehlungen,
 * Ressource-Listen und deren Karussells (auch auf Produktdetailseiten).
 * Der eigentliche Produktpreis und die Warenkorbfunktion bleiben unberuehrt.
 *
 * Einmal ueber layout/theme.liquid einbinden. Vorhandene V1 ersetzen,
 * nicht als zweites Script parallel installieren.
 */
(() => {
  'use strict';

  const globalKey = '__GN_GLOBAL_CARD_ROW_ALIGN_V2__';
  if (window[globalKey]) return;
  window[globalKey] = true;

  const CARD_SELECTOR = 'product-card.product-card:not([data-placeholder]), .gnc-card';
  const GROUP_SELECTOR = '.product-grid, .resource-list--grid, .resource-list--bento, .resource-list--editorial';
  const ROW_TOLERANCE = 7;
  const MAX_OFFSET = 500; // Keine unplausiblen, riesigen Abstaende erzeugen.

  const originalMargins = new WeakMap();
  let scheduled = false;
  let active = false;

  /** Restore original inline margin without overwriting inline theme settings. */
  function restoreMargin(element) {
    if (!element) return;

    if (!originalMargins.has(element)) {
      originalMargins.set(element, {
        value: element.style.getPropertyValue('margin-top'),
        priority: element.style.getPropertyPriority('margin-top'),
      });
    }

    const original = originalMargins.get(element);
    if (original.value) {
      element.style.setProperty('margin-top', original.value, original.priority);
    } else {
      element.style.removeProperty('margin-top');
    }
  }

  /**
   * Some custom sections use margin-top:auto to push the price down.
   * Normalize those margins before measuring so all cards follow one logic.
   */
  function normalizeAutoMargin(element) {
    if (!element) return;
    if (getComputedStyle(element).marginTop === 'auto') {
      element.style.setProperty('margin-top', '0px', 'important');
    }
  }

  function increaseMargin(element, distance) {
    if (!element || distance <= 0.6 || distance > MAX_OFFSET) return;
    const computedValue = Number.parseFloat(getComputedStyle(element).marginTop);
    const base = Number.isFinite(computedValue) ? computedValue : 0;
    element.style.setProperty('margin-top', `${Math.round((base + distance) * 100) / 100}px`, 'important');
  }

  /** Resolve the nearest *product list*, not the slideshow inside an image. */
  function groupingContainer(card) {
    let parent = card.parentElement;
    while (parent && parent !== document.body) {
      if (parent.matches(GROUP_SELECTOR)) return parent;
      if (parent.matches('slideshow-slides') && parent.closest('.resource-list__carousel')) return parent;
      parent = parent.parentElement;
    }
    return null;
  }

  function visibleRect(element) {
    if (!element) return null;
    const box = element.getBoundingClientRect();
    if (box.width < 25 || box.height < 10) return null;
    if (getComputedStyle(element).visibility === 'hidden') return null;
    return box;
  }

  /** Different Horizon templates share product-card; GN custom cards use gnc-card. */
  function parts(card) {
    if (card.matches('.gnc-card')) {
      return {
        price: card.querySelector('.gnc-card__bottom'),
        buy: null,
        button: null,
      };
    }

    const content = card.querySelector('.product-grid__card, .product-card__content') ?? card;
    const price = content.querySelector('product-price');
    const buy = content.querySelector('.buy-buttons-block');
    const button = buy?.querySelector('.add-to-cart-button, button[name="add"], .product-form-buttons button[type="submit"]') ?? null;

    return { price, buy, button };
  }

  function collectRows() {
    const groups = new Map();
    const cards = document.querySelectorAll(CARD_SELECTOR);

    // Restore every visible/re-rendered card FIRST, before any measurements.
    // This prevents previously added offsets from accumulating on resize.
    const candidates = [];
    for (const card of cards) {
      const container = groupingContainer(card);
      if (!container || !visibleRect(container)) continue;

      const { price, buy, button } = parts(card);
      restoreMargin(price);
      restoreMargin(buy);
      normalizeAutoMargin(price);

      const rectangle = visibleRect(card);
      if (!rectangle || !visibleRect(price)) continue;
      candidates.push({ card, container, rectangle, price, buy, button });
    }

    for (const candidate of candidates) {
      const { container, rectangle } = candidate;
      if (!groups.has(container)) groups.set(container, []);
      const rows = groups.get(container);
      let row = rows.find((entry) => Math.abs(entry.top - rectangle.top) <= ROW_TOLERANCE);
      if (!row) {
        row = { top: rectangle.top, members: [] };
        rows.push(row);
      }
      row.members.push(candidate);
    }

    return groups;
  }

  function alignRow(row) {
    if (row.members.length < 2) return;

    // 1. Alle Preise an derselben Oberkante.
    const withPrices = row.members.filter((entry) => visibleRect(entry.price));
    if (withPrices.length >= 2) {
      const target = Math.max(...withPrices.map((entry) => entry.price.getBoundingClientRect().top));
      for (const entry of withPrices) {
        increaseMargin(entry.price, target - entry.price.getBoundingClientRect().top);
      }
    }

    // 2. Nach dem Verschieben der Preise die ECHTEN Buttons neu messen.
    const withButtons = row.members.filter((entry) => entry.buy && visibleRect(entry.button));
    if (withButtons.length >= 2) {
      const target = Math.max(...withButtons.map((entry) => entry.button.getBoundingClientRect().top));
      for (const entry of withButtons) {
        increaseMargin(entry.buy, target - entry.button.getBoundingClientRect().top);
      }
    }
  }

  function alignAll() {
    if (active) return;
    active = true;
    try {
      for (const rows of collectRows().values()) {
        for (const row of rows) alignRow(row);
      }
    } finally {
      active = false;
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      alignAll();
    });
  }

  function init() {
    // Shopifys Section Rendering ersetzt Produktkarten ohne vollen Reload.
    const observer = new MutationObserver((records) => {
      if (records.some((record) => record.type === 'childList' || record.type === 'characterData' || record.type === 'attributes')) {
        schedule();
      }
    });
    observer.observe(document.body, {
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'aria-hidden'],
      subtree: true,
    });

    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
    window.addEventListener('pageshow', schedule);
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('shopify:section:select', schedule);
    document.addEventListener('collection:update', schedule);
    document.addEventListener('search:update', schedule);
    document.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.closest('product-card, .gnc-card')) schedule();
    }, true);
    document.addEventListener('load', (event) => {
      if (event.target instanceof HTMLImageElement && event.target.closest('product-card, .gnc-card')) schedule();
    }, true);

    // Cards in a scrolling product carousel may render lazily.
    document.addEventListener('scroll', (event) => {
      if (event.target instanceof Element && event.target.closest('.resource-list__carousel')) schedule();
    }, true);

    if (document.fonts?.ready) document.fonts.ready.then(schedule);
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
